import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileSourceInfo = ({ 
  files = [], 
  onRemoveFile,
  onAddMoreFiles 
}) => {
  const getTotalRows = () => {
    return files?.reduce((total, file) => total + (file?.rowCount || 0), 0);
  };

  const getTotalSize = () => {
    return files?.reduce((total, file) => total + (file?.size || 0), 0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(1)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'csv':
        return 'FileText';
      case 'xlsx': case'xls':
        return 'FileSpreadsheet';
      default:
        return 'File';
    }
  };

  const getFileTypeColor = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'csv':
        return 'text-success';
      case 'xlsx': case'xls':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  if (files?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 text-center">
        <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h4 className="text-lg font-medium text-foreground mb-2">No Files Loaded</h4>
        <p className="text-muted-foreground mb-4">
          Upload files to begin column mapping configuration.
        </p>
        <Button
          variant="outline"
          onClick={onAddMoreFiles}
          iconName="Plus"
          iconPosition="left"
        >
          Upload Files
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Source Files</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {files?.length} file{files?.length !== 1 ? 's' : ''} • {getTotalRows()?.toLocaleString()} total rows • {formatFileSize(getTotalSize())}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onAddMoreFiles}
            iconName="Plus"
            iconPosition="left"
          >
            Add Files
          </Button>
        </div>
      </div>
      {/* Files List */}
      <div className="divide-y divide-border">
        {files?.map((file, index) => (
          <div key={`${file?.name}-${index}`} className="p-4 hover:bg-muted hover:bg-opacity-30 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className={`mt-1 ${getFileTypeColor(file?.name)}`}>
                  <Icon name={getFileIcon(file?.name)} size={20} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {file?.name}
                    </h4>
                    {file?.status === 'processed' && (
                      <Icon name="CheckCircle" size={14} className="text-success flex-shrink-0" />
                    )}
                    {file?.status === 'processing' && (
                      <Icon name="Loader2" size={14} className="text-warning animate-spin flex-shrink-0" />
                    )}
                    {file?.status === 'error' && (
                      <Icon name="AlertCircle" size={14} className="text-error flex-shrink-0" />
                    )}
                  </div>
                  
                  <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium">Size:</span> {formatFileSize(file?.size)}
                    </div>
                    <div>
                      <span className="font-medium">Rows:</span> {(file?.rowCount || 0)?.toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">Columns:</span> {file?.columnCount || 0}
                    </div>
                    <div>
                      <span className="font-medium">Uploaded:</span> {formatDate(file?.uploadedAt)}
                    </div>
                  </div>

                  {file?.columns && file?.columns?.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-muted-foreground">Columns:</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {file?.columns?.slice(0, 6)?.map((column, colIndex) => (
                          <span 
                            key={colIndex}
                            className="inline-flex items-center px-2 py-1 bg-muted text-xs text-foreground rounded"
                          >
                            {column}
                          </span>
                        ))}
                        {file?.columns?.length > 6 && (
                          <span className="text-xs text-muted-foreground">
                            +{file?.columns?.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {file?.error && (
                    <div className="mt-2 p-2 bg-error bg-opacity-10 border border-error border-opacity-20 rounded">
                      <div className="flex items-center space-x-2">
                        <Icon name="AlertCircle" size={14} className="text-error" />
                        <span className="text-xs text-error font-medium">Error:</span>
                      </div>
                      <p className="text-xs text-error mt-1">{file?.error}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveFile(index)}
                  iconName="X"
                  className="h-6 w-6 text-muted-foreground hover:text-error"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Summary Footer */}
      <div className="p-4 border-t border-border bg-muted bg-opacity-30">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div>
            <span className="font-medium text-muted-foreground">Total Files:</span>
            <p className="text-foreground mt-1 font-medium">{files?.length}</p>
          </div>
          <div>
            <span className="font-medium text-muted-foreground">Total Rows:</span>
            <p className="text-foreground mt-1 font-medium">{getTotalRows()?.toLocaleString()}</p>
          </div>
          <div>
            <span className="font-medium text-muted-foreground">Total Size:</span>
            <p className="text-foreground mt-1 font-medium">{formatFileSize(getTotalSize())}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileSourceInfo;